// The signed-in person's platform access, for every HQ page below the layout (`HqLayout`), which has
// already read it and let only a platform role with a second factor through (spec/07 §7, spec/14 §1).
//
// It decides only what is **shown**. Every `/v1/hq/*` route checks the platform role, the second
// factor and its own permission again (`apps/server/src/http/platform.ts`) — the server is the check.

import { useTranslation } from '@clavian/i18n/react';
import { createContext, useContext, type ReactNode } from 'react';

import type { PlatformAccess } from '../api/me';

const PlatformAccessContext = createContext<PlatformAccess | null>(null);

export function PlatformAccessProvider({ access, children }: { readonly access: PlatformAccess; readonly children: ReactNode }) {
  return <PlatformAccessContext.Provider value={access}>{children}</PlatformAccessContext.Provider>;
}

export function usePlatformAccessHere(): PlatformAccess {
  const access = useContext(PlatformAccessContext);
  if (access === null) throw new Error('usePlatformAccessHere() is for pages inside the HQ layout.');
  return access;
}

/** A page for a role that holds `permission`; anyone else is told it is not theirs. */
export function RequirePlatformPermission({ permission, children }: { readonly permission: string; readonly children: ReactNode }) {
  const access = usePlatformAccessHere();
  return access.permissions.has(permission) ? children : <NotPermitted />;
}

export function NotPermitted() {
  const { t } = useTranslation('hq');
  return (
    <div className="console-page">
      <h1 className="console-page__title">{t('notPermitted.title')}</h1>
      <p>{t('notPermitted.body')}</p>
    </div>
  );
}
